import React from 'react'
import { Section } from './AddNewPatient'
import {
  Autocomplete,
  Box,
  Checkbox,
  createFilterOptions,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material'

const mobilityAidOptions = [
  { group: 'Walking Aids', label: 'Walking Stick' },
  { group: 'Walking Aids', label: 'Quad Stick' },
  { group: 'Walking Aids', label: 'Crutches (Single)' },
  { group: 'Walking Aids', label: 'Crutches (Pair)' },
  { group: 'Walking Aids', label: 'Forearm Crutches' },
  { group: 'Walking Aids', label: 'Walking Frame (No Wheels)' },
  { group: 'Walking Aids', label: 'Wheeled Walking Frame' },
  { group: 'Walking Aids', label: 'Rollator (4 Wheels)' },

  { group: 'Wheelchairs', label: 'Manual Wheelchair (Self-Propelled)' },
  { group: 'Wheelchairs', label: 'Manual Wheelchair (Attendant-Propelled)' },
  { group: 'Wheelchairs', label: 'Electric Wheelchair / Powerchair' },
  { group: 'Wheelchairs', label: 'Bariatric Wheelchair' },
  { group: 'Wheelchairs', label: 'Reclining Wheelchair' },
  { group: 'Wheelchairs', label: 'Tilt-in-Space Wheelchair' },

  { group: 'Transfer & Support Aids', label: 'Transfer Board' },
  { group: 'Transfer & Support Aids', label: 'Transfer Belt (Gait Belt)' },
  { group: 'Transfer & Support Aids', label: 'Hoist (Manual/Electric)' },
  { group: 'Transfer & Support Aids', label: 'Sliding Sheet' },
  { group: 'Transfer & Support Aids', label: 'Standaid / Rotastand' },

  { group: 'Other Aids', label: 'Prosthetic Limb' },
  { group: 'Other Aids', label: 'Orthotic Device / Brace' },
  { group: 'Other Aids', label: 'Walking Boot / Cast' },
  { group: 'Other Aids', label: 'Oxygen Trolley' },
  { group: 'Other Aids', label: 'IV Stand (Mobile)' },
  { group: 'Other Aids', label: 'Stretcher' },
  { group: 'Other Aids', label: 'Bariatric Stretcher' },
]

const Mobility = ({
  mobility,
  setMobility,
  setChangesMade,
  existingPatient,
}) => {
  const filter = createFilterOptions()
  return (
    <Section title="Mobility">
      <Grid item xs={12} lg={4}>
        <TextField
          value={mobility.category}
          onChange={(e) =>
            setMobility((prev) => ({ ...prev, category: e.target.value }))
          }
          onBlur={() =>
            existingPatient &&
            mobility?.category !== existingPatient?.mobility?.category &&
            setChangesMade((prev) => ({
              ...prev,
              'Mobility Category': {
                field: 'mobility.category',
                value: mobility.category,
              },
            }))
          }
          select
          fullWidth
          label="Category"
        >
          {['Walking', 'Chair', 'Stretcher'].map((x) => (
            <MenuItem key={x} value={x}>
              {x}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Box
          display="flex"
          alignItems="stretch"
          border="solid 1px"
          borderColor="rgba(0, 0, 0, 0.23)"
          borderRadius={1}
          height="54px"
          px={2}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={mobility.isBariatric}
                onChange={(e) =>
                  setMobility((prev) => ({
                    ...prev,
                    isBariatric: e.target.checked,
                  }))
                }
                onBlur={() =>
                  existingPatient &&
                  mobility?.isBariatric !==
                    existingPatient?.mobility?.isBariatric &&
                  setChangesMade((prev) => ({
                    ...prev,
                    'Mobility - Bariatric': {
                      field: 'mobility.isBariatric',
                      value: mobility.isBariatric ? 'Yes' : 'No',
                    },
                  }))
                }
                color="info"
              />
            }
            label="Is Bariatric"
          />
        </Box>
      </Grid>
      <Grid item xs={12} lg={4}>
        <Box
          display="flex"
          alignItems="stretch"
          border="solid 1px"
          borderColor="rgba(0, 0, 0, 0.23)"
          borderRadius={1}
          height="54px"
          px={2}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={mobility.mustTravelAlone}
                onChange={(e) =>
                  setMobility((prev) => ({
                    ...prev,
                    mustTravelAlone: e.target.checked,
                  }))
                }
                onBlur={() =>
                  existingPatient &&
                  mobility?.mustTravelAlone !==
                    existingPatient?.mobility?.mustTravelAlone &&
                  setChangesMade((prev) => ({
                    ...prev,
                    'Must Travel Alone': {
                      field: 'mobility.mustTravelAlone',
                      value: mobility.mustTravelAlone ? 'Yes' : 'No',
                    },
                  }))
                }
                color="info"
              />
            }
            label="Must travel alone"
          />
        </Box>
      </Grid>
      <Grid item xs={6} lg={4}>
        <TextField
          value={mobility.heightCm ?? ''}
          onChange={(e) =>
            setMobility((prev) => ({ ...prev, heightCm: e.target.value }))
          }
          onBlur={() =>
            existingPatient &&
            String(mobility?.heightCm ?? '') !==
              String(existingPatient?.mobility?.heightCm ?? '') &&
            setChangesMade((prev) => ({
              ...prev,
              'Height (cm)': {
                field: 'mobility.heightCm',
                value: mobility.heightCm,
              },
            }))
          }
          type="number"
          inputProps={{ min: 0 }}
          fullWidth
          label="Height (cm)"
        />
      </Grid>
      <Grid item xs={6} lg={4}>
        <TextField
          value={mobility.weightKg ?? ''}
          onChange={(e) =>
            setMobility((prev) => ({ ...prev, weightKg: e.target.value }))
          }
          onBlur={() =>
            existingPatient &&
            String(mobility?.weightKg ?? '') !==
              String(existingPatient?.mobility?.weightKg ?? '') &&
            setChangesMade((prev) => ({
              ...prev,
              'Weight (kg)': {
                field: 'mobility.weightKg',
                value: mobility.weightKg,
              },
            }))
          }
          type="number"
          inputProps={{ min: 0 }}
          fullWidth
          label="Weight (kg)"
          helperText="Checked against vehicle weight limits when planning"
        />
      </Grid>
      <Grid item xs={12}>
        <Autocomplete
          value={mobility.aids}
          onChange={(e, newValue) => {
            const cleaned = newValue.map(
              (v) =>
                typeof v === 'string'
                  ? v
                  : v.inputValue // user-typed custom option
                    ? v.inputValue
                    : v.label, // normal predefined option
            )
            setMobility((prev) => ({
              ...prev,
              aids: cleaned,
            }))
          }}
          onBlur={() =>
            existingPatient &&
            mobility?.aids !== existingPatient?.mobility?.aids &&
            setChangesMade((prev) => ({
              ...prev,
              'Mobility Aids': { field: 'mobility.aids', value: mobility.aids },
            }))
          }
          clearOnBlur={false}
          selectOnFocus
          handleHomeEndKeys
          filterOptions={(options, params) => {
            const filtered = filter(options, params)
            const { inputValue } = params

            const isExisting = options.some(
              (option) => option.label === inputValue,
            )
            if (inputValue !== '' && !isExisting) {
              filtered.push({
                inputValue,
                label: `Add "${inputValue}"`,
                group: 'Custom Entry',
              })
            }

            return filtered
          }}
          fullWidth
          options={mobilityAidOptions}
          groupBy={(option) => option.group}
          // 👇 Here’s the key difference
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option
            if (option.label?.startsWith('Add ')) return option.label // show “Add ‘xxx’” in dropdown
            if (option.inputValue) return option.inputValue // show raw value when selected
            return option.label
          }}
          multiple
          freeSolo
          renderInput={(params) => (
            <TextField
              {...params}
              label="Mobility Aids"
              placeholder="Select aids or type and press Enter"
              helperText="Start typing to add a custom aid"
            />
          )}
        />
      </Grid>
    </Section>
  )
}

export default Mobility
